import { useParams, Link } from 'react-router-dom'
import {Phone, MapPin, Clock, ShieldCheck, CheckCircle2, ArrowLeft, Calendar} from 'lucide-react'
import ContactForm from '@/components/ContactForm'

const cityData: Record<string, { city: string; canton: string; intro: string }> = {
  'geneve': { city: 'Genève', canton: 'Genève', intro: 'au cœur du canton de Genève' },
  'carouge': { city: 'Carouge', canton: 'Genève', intro: 'à Carouge et environs' },
  'vernier': { city: 'Vernier', canton: 'Genève', intro: 'à Vernier' },
  'meyrin': { city: 'Meyrin', canton: 'Genève', intro: 'à Meyrin' },
  'lausanne': { city: 'Lausanne', canton: 'Vaud', intro: 'à Lausanne et alentours' },
  'montreux': { city: 'Montreux', canton: 'Vaud', intro: 'à Montreux et la Riviera' },
  'vevey': { city: 'Vevey', canton: 'Vaud', intro: 'à Vevey' },
  'nyon': { city: 'Nyon', canton: 'Vaud', intro: 'à Nyon et la Côte' },
  'morges': { city: 'Morges', canton: 'Vaud', intro: 'à Morges' },
  'yverdon': { city: 'Yverdon-les-Bains', canton: 'Vaud', intro: 'à Yverdon-les-Bains' },
  'neuchatel': { city: 'Neuchâtel', canton: 'Neuchâtel', intro: 'à Neuchâtel' },
  'chaux-de-fonds': { city: 'La Chaux-de-Fonds', canton: 'Neuchâtel', intro: 'à La Chaux-de-Fonds' },
  'fribourg': { city: 'Fribourg', canton: 'Fribourg', intro: 'à Fribourg et alentours' },
  'bulle': { city: 'Bulle', canton: 'Fribourg', intro: 'à Bulle et la Gruyère' },
  'sion': { city: 'Sion', canton: 'Valais', intro: 'à Sion et le Valais central' },
  'martigny': { city: 'Martigny', canton: 'Valais', intro: 'à Martigny' },
  'monthey': { city: 'Monthey', canton: 'Valais', intro: 'à Monthey' },
}

export default function ZoneDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const data = slug ? cityData[slug] : null

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center">
        <h1 className="text-3xl font-extrabold text-navy-700">Zone non trouvée</h1>
        <p className="mt-3 text-navy-500">Consultez la liste de nos zones d'intervention.</p>
        <Link to="/zones-intervention" className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl gradient-urgent text-white font-bold">
          Voir les zones
        </Link>
      </div>
    )
  }

  const { city, canton, intro } = data

  return (
    <div>
      <section className="bg-navy-700 text-white py-14 lg:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 0%, transparent 60%)' }} />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <Link to="/zones-intervention" className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy-200 hover:text-white mb-5">
            <ArrowLeft className="w-4 h-4" /> Retour aux zones
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-urgent-500/20 border border-urgent-400/40 text-urgent-300 text-xs font-bold uppercase tracking-wider">
            <MapPin className="w-3.5 h-3.5" />
            Canton de {canton}
          </div>
          <h1 className="mt-5 text-4xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-balance max-w-3xl">
            Plombier {city} <span className="text-urgent-400">— Urgence 24/7</span>
          </h1>
          <p className="mt-5 text-lg text-navy-100 leading-relaxed max-w-2xl">
            Dépannage sanitaire et chauffage rapide {intro}. Intervention en urgence pour fuite d'eau, WC bouché, canalisation, chauffe-eau, chaudière et radiateurs. Disponible jour et nuit.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a href="tel:+41796430399" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl gradient-urgent text-white font-bold shadow-orange-glow">
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              Appeler maintenant
            </a>
            <a href="https://fixnow.ch" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-white text-navy-700 font-bold hover:bg-navy-50 transition-colors">
              <Calendar className="w-5 h-5" strokeWidth={2.5} />
              Voir notre disponibilité sur FixNow
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl lg:text-3xl font-extrabold text-navy-700">Pourquoi choisir notre plombier à {city} ?</h2>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Clock, t: 'Intervention rapide', d: `Plombier disponible 24/7 ${intro}, délai moyen 35 min.` },
                  { icon: ShieldCheck, t: 'Devis transparent', d: 'Tarifs annoncés avant intervention. Sans surprise.' },
                  { icon: MapPin, t: 'Connaissance locale', d: `Notre équipe connaît parfaitement ${city} et ses quartiers.` },
                  { icon: CheckCircle2, t: 'Garantie qualité', d: 'Plombiers certifiés, assurés, garantie sur les travaux.' },
                ].map((b) => (
                  <div key={b.t} className="bg-white rounded-2xl p-5 border border-navy-100">
                    <div className="w-10 h-10 rounded-xl gradient-urgent flex items-center justify-center mb-3">
                      <b.icon className="w-5 h-5 text-white" strokeWidth={2.2} />
                    </div>
                    <div className="font-extrabold text-navy-700">{b.t}</div>
                    <div className="text-sm text-navy-500 mt-1">{b.d}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-2xl lg:text-3xl font-extrabold text-navy-700">Nos services à {city}</h2>
              <div className="mt-5 space-y-3">
                {[
                  { t: "Fuite d'eau d'urgence", d: "Détection et réparation rapide partout " + intro + "." },
                  { t: 'WC et canalisations bouchés', d: 'Débouchage haute pression, furet, caméra d\'inspection.' },
                  { t: 'Chauffe-eau en panne', d: 'Dépannage et remplacement de chauffe-eau toutes marques.' },
                  { t: 'Chauffage & chaudière', d: 'Dépannage chaudière, radiateurs froids, perte de pression, toutes marques.' },
                  { t: 'Installation sanitaire', d: 'Pose, rénovation, raccordements et entretien.' },
                ].map((s) => (
                  <div key={s.t} className="flex items-start gap-3 p-4 rounded-xl bg-navy-50 border border-navy-100">
                    <CheckCircle2 className="w-5 h-5 text-urgent-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <div>
                      <div className="font-bold text-navy-700">{s.t}</div>
                      <div className="text-sm text-navy-500">{s.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:sticky lg:top-28 self-start">
            <ContactForm compact />
          </div>
        </div>
      </section>
    </div>
  )
}
